import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Users, 
  Search, 
  Plus, 
  Mail, 
  Phone, 
  GraduationCap, 
  MoreHorizontal,
  ChevronRight,
  TrendingUp,
  Award
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

interface Student {
  id: string;
  name: string;
  email: string;
  grade: string;
  performance: number;
  lastActive: string;
  avatar?: string;
}

const mockStudents: Student[] = [
  { id: '1', name: 'Alice Johnson', email: 'alice@example.com', grade: 'Grade 10', performance: 95, lastActive: '2h ago' },
  { id: '2', name: 'Bob Smith', email: 'bob@example.com', grade: 'Grade 9', performance: 82, lastActive: '5h ago' },
  { id: '3', name: 'Charlie Davis', email: 'charlie@example.com', grade: 'Grade 10', performance: 78, lastActive: '1d ago' },
  { id: '4', name: 'Diana Prince', email: 'diana@example.com', grade: 'Grade 11', performance: 91, lastActive: 'Just now' },
];

const StudentManagerView = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [students] = useState<Student[]>(mockStudents);

  const filteredStudents = students.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    s.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8 w-full">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
            Student Roster
          </h1>
          <p className="text-muted-foreground mt-1">Manage your students and track their growth.</p>
        </div>
        <Button className="rounded-full bg-blue-600 hover:bg-blue-700 shadow-xl transition-all">
          <Plus className="mr-2 h-4 w-4" /> Add Student
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input 
            placeholder="Search students..." 
            className="pl-12 h-14 bg-card rounded-2xl border-none shadow-sm focus-visible:ring-2 focus-visible:ring-blue-500 transition-all font-medium text-lg"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-4 bg-blue-50 dark:bg-blue-900/20 px-6 py-4 rounded-2xl">
          <TrendingUp className="text-blue-600 h-8 w-8" />
          <div>
            <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">Class Average</p>
            <p className="text-2xl font-bold text-blue-950 dark:text-blue-100">86.5%</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2">
        <AnimatePresence mode="popLayout">
          {filteredStudents.map((student) => (
            <motion.div
              key={student.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <Card className="group border-none bg-card shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden">
                <CardHeader className="flex flex-row items-center gap-4 pb-4">
                  <Avatar className="h-16 w-16 ring-4 ring-offset-2 ring-blue-500/10 group-hover:ring-blue-500/20 transition-all">
                    <AvatarFallback className="bg-blue-600 text-white text-xl font-bold">
                      {student.name.split(' ').map(n => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-xl font-bold truncate">{student.name}</CardTitle>
                      {student.performance > 90 && <Award className="h-5 w-5 text-amber-500 fill-amber-500" />}
                    </div>
                    <CardDescription className="flex items-center gap-1 font-medium">
                      <GraduationCap className="h-4 w-4" /> {student.grade}
                    </CardDescription>
                  </div>
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <MoreHorizontal className="h-5 w-5" />
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Mail className="h-3 w-3" /> Email
                      </p>
                      <p className="text-sm font-semibold truncate">{student.email}</p>
                    </div>
                    <div className="space-y-1 text-right">
                      <p className="text-xs text-muted-foreground">Performance</p>
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-24 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                           <motion.div 
                              initial={{ width: 0 }}
                              animate={{ width: `${student.performance}%` }}
                              className={`h-full rounded-full ${student.performance > 85 ? 'bg-green-500' : 'bg-blue-500'}`}
                           />
                        </div>
                        <span className="text-sm font-bold">{student.performance}%</span>
                      </div>
                    </div>
                  </div>
                  <div className="pt-4 border-t flex items-center justify-between">
                    <span className="text-xs text-muted-foreground italic font-medium">Last seen: {student.lastActive}</span>
                    <Button variant="link" className="group/link text-blue-600 font-bold p-0 h-auto">
                      View Profile <ChevronRight className="h-4 w-4 ml-1 group-hover/link:translate-x-1 transition-transform" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {filteredStudents.length === 0 && (
        <div className="text-center py-20 bg-muted/20 rounded-3xl border-2 border-dashed border-muted">
          <Users size={64} className="mx-auto text-muted-foreground opacity-20" />
          <h3 className="text-xl font-bold mt-4 text-muted-foreground">No students matched</h3>
          <p className="text-muted-foreground">Check the spelling or add a new student.</p>
        </div>
      )}
    </div>
  );
};

export default StudentManagerView;
